import { useState, useEffect, useRef } from "react";
import { X, Upload, Building, Calendar, DollarSign, FileText, Image as ImageIcon, Camera, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { insertarZonaComun, insertarFotosZonaComun } from "../../../../api/zonaComunService";
import { obtenerTodosDiasUtilizacion, DiaUtilizacion } from "../../../../api/zonaComunHorarioService";

interface CreateZonaComunModalProps {
  isOpen: boolean;
  onClose: (created: boolean) => void;
}

interface ImagePreview {
  id: string;
  file: File;
  preview: string;
}

export default function CreateZonaComunModal({ isOpen, onClose }: CreateZonaComunModalProps) {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [selectedDiaMantenimiento, setSelectedDiaMantenimiento] = useState("");
  const [valorAlquiler, setValorAlquiler] = useState("");
  const [imagenes, setImagenes] = useState<ImagePreview[]>([]);
  
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [diasUtilizacion, setDiasUtilizacion] = useState<DiaUtilizacion[]>([]);
  const [isLoadingDias, setIsLoadingDias] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDiasUtilizacion();
      resetForm();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const resetForm = () => {
    setNombre("");
    setDescripcion("");
    setSelectedDiaMantenimiento("");
    setValorAlquiler("");
    setImagenes([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const loadDiasUtilizacion = async () => {
    try {
      setIsLoadingDias(true);
      const response = await obtenerTodosDiasUtilizacion();
      if (response.code === "1" && response.object_) {
        setDiasUtilizacion(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar días de utilización:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los días de utilización",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10002';
          },
        });
    } finally {
      setIsLoadingDias(false);
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("La API de cámara no está disponible en este navegador.");
      }

      setShowCamera(true);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      setStream(mediaStream);
    } catch (error) {
      console.error("Error al acceder a la cámara:", error);
      setShowCamera(false);
      Swal.fire({
        icon: "error",
        title: "Error de cámara",
        text: "No se pudo acceder a la cámara. Verifica los permisos del navegador y que la cámara no esté siendo usada por otra aplicación.",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
    }
  };

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error("No se pudo reproducir el video de la cámara:", err);
        });
      }
    }
  }, [showCamera, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (context) {
        context.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
            addImage(file);
            stopCamera();
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        if (file.size > 5 * 1024 * 1024) {
          Swal.fire({
            icon: "warning",
            title: "Archivo muy grande",
            text: `La imagen ${file.name} no debe superar los 5MB`,
            confirmButtonColor: "#3d5a40",
            didOpen: (popup) => {
              (popup as HTMLElement).style.zIndex = '10002';
            },
          });
          return;
        }

        if (!file.type.startsWith("image/")) {
          Swal.fire({
            icon: "warning",
            title: "Formato inválido",
            text: `El archivo ${file.name} no es una imagen válida`,
            confirmButtonColor: "#3d5a40",
            didOpen: (popup) => {
              (popup as HTMLElement).style.zIndex = '10002';
            },
          });
          return;
        }

        addImage(file);
      });
    }
  };

  const addImage = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result as string;
      const newImage: ImagePreview = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        file,
        preview,
      };
      setImagenes((prev) => [...prev, newImage]);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (id: string) => {
    setImagenes((prev) => prev.filter((img) => img.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nombre.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el nombre de la zona común",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
      return;
    }

    if (!descripcion.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la descripción de la zona común",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
      return;
    }

    if (!selectedDiaMantenimiento) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione el día de mantenimiento",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
      return;
    }

    if (!valorAlquiler || valorAlquiler.trim() === "" || isNaN(parseFloat(valorAlquiler)) || parseFloat(valorAlquiler) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese un valor de alquiler válido (puede ser 0)",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        Swal.fire({
          icon: "error",
          title: "Error de sesión",
          text: "No se pudo obtener el código de unidad residencial. Por favor, inicie sesión nuevamente.",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10002';
          },
        });
        return;
      }

      const payload = {
        unidad_residencial_codigo: unidadCodigo,
        nombre_zona_comun: nombre.trim(),
        descripcion_zona_comun: descripcion.trim(),
        valor_alquiler: parseFloat(valorAlquiler),
        dia_mantenimiento_codigo: selectedDiaMantenimiento,
      };

      const response = await insertarZonaComun(payload);

      if (response.code === "1" || response.code === 1) {
        const zonaComunCodigo = response.object_?.[0]?.zco_codigo;

        if (!zonaComunCodigo) {
          throw new Error("No se recibió el código de la zona común creada");
        }

        if (imagenes.length > 0) {
          try {
            const fotosFiles = imagenes.map((img) => img.file);
            await insertarFotosZonaComun(zonaComunCodigo, fotosFiles);
          } catch (fotoError) {
            console.error("Error al subir fotos:", fotoError);
            Swal.fire({
              icon: "warning",
              title: "Zona común creada",
              text: "La zona común se creó exitosamente, pero hubo un error al subir algunas imágenes. Puede intentar agregarlas más tarde.",
              confirmButtonColor: "#3d5a40",
              didOpen: (popup) => {
                (popup as HTMLElement).style.zIndex = '10002';
              },
            });
            onClose(true);
            return;
          }
        }

        Swal.fire({
          icon: "success",
          title: "¡Zona común creada!",
          text: response.message || "La zona común se ha creado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10002';
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al crear la zona común");
      }
    } catch (error) {
      console.error("Error al crear zona común:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al crear la zona común";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al crear la zona común. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building size={28} />
            Nueva Zona Común
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              <ImageIcon size={18} className="inline mr-2 text-military-600" />
              Imágenes de la Zona Común
            </label>
            
            {imagenes.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                {imagenes.map((img) => (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.preview}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100"
                      disabled={isSubmitting}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-military-400 hover:bg-gray-50 transition-all">
              <div className="py-4">
                <div className="bg-military-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <Upload size={32} className="text-military-600" />
                </div>
                <p className="text-gray-700 font-medium mb-1">
                  Click para seleccionar imágenes o tomar foto
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG o GIF (máx. 5MB cada una)
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                <button
                  type="button"
                  onClick={startCamera}
                  disabled={isSubmitting}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all font-medium w-full sm:w-auto bg-white ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Camera size={18} />
                  Tomar Foto
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="file-upload"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="file-upload"
                  className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all font-medium w-full sm:w-auto bg-white ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Upload size={18} />
                  Seleccionar Imágenes
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Building size={16} className="inline mr-2 text-military-600" />
              Nombre *
            </label>
            <Input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Zona BBQ 1"
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm"
              required
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FileText size={16} className="inline mr-2 text-military-600" />
              Descripción *
            </label>
            <Textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej: Se alquila la zona, es necesario llevar carbón y dejar todo limpio al terminar."
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm min-h-[100px]"
              required
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Calendar size={16} className="inline mr-2 text-military-600" />
              Día de Mantenimiento *
            </label>
            <select
              value={selectedDiaMantenimiento}
              onChange={(e) => setSelectedDiaMantenimiento(e.target.value)}
              disabled={isSubmitting || isLoadingDias}
              className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-3 bg-white shadow-sm"
              required
            >
              {isLoadingDias ? (
                <option value="">Cargando días...</option>
              ) : (
                <>
                  <option value="">Seleccionar...</option>
                  {diasUtilizacion.map((dia) => (
                    <option key={dia.ddu_codigo} value={dia.ddu_codigo}>
                      {dia.ddu_nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <DollarSign size={16} className="inline mr-2 text-military-600" />
              Costo del Alquiler *
            </label>
            <Input
              type="number"
              value={valorAlquiler}
              onChange={(e) => setValorAlquiler(e.target.value)}
              placeholder="Ej: 100000"
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm"
              min="0"
              step="1"
              required
            />
          </div>

          <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 -mx-6 px-6 pb-6">
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
                className="flex-1 border-2 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Building size={20} className="mr-2" />
                    Guardar Zona Común
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-military-600">
                Tomar Foto
              </h3>
              <button
                onClick={stopCamera}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto max-h-[60vh]"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={capturePhoto}
                  className="bg-military-500 hover:bg-military-600 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Capturar Foto
                </Button>
                <Button
                  onClick={stopCamera}
                  variant="outline"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

